<template>
  <div class="segment-source-linker">
    <!-- Episode Source Type Toggle (only shown when not controlled by parent) -->
    <div v-if="!hideTypeToggle" class="episode-type-toggle mb-4">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-field label="Source File Organization">
              <b-radio-button
                v-model="episodeSourceType"
                native-value="single"
                type="is-info"
                @input="onEpisodeTypeChange"
              >
                <span class="icon"><i class="mdi mdi-file-video"></i></span>
                <span>Single Complete File</span>
              </b-radio-button>
              <b-radio-button
                v-model="episodeSourceType"
                native-value="multi"
                type="is-primary"
                @input="onEpisodeTypeChange"
              >
                <span class="icon"><i class="mdi mdi-file-multiple"></i></span>
                <span>Separate Segment Files</span>
              </b-radio-button>
            </b-field>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <p class="help" v-if="effectiveSourceType === 'single'">
              Source file already contains all segments combined - no stitching needed
            </p>
            <p class="help" v-else>
              Link separate files for each segment to be stitched together
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Single File Mode - one complete file for all segments -->
    <div v-if="effectiveSourceType === 'single'" class="single-file-mode">
      <!-- Episode Info & TX Filename Display -->
      <div class="box mb-4">
        <div class="columns">
          <div class="column is-6">
            <h5 class="title is-6 mb-2">
              <span class="icon has-text-info"><i class="mdi mdi-file-video"></i></span>
              Source File (Input)
            </h5>
            <div v-if="singleFileLinked" class="notification is-success is-light py-2">
              <div class="level mb-0">
                <div class="level-left">
                  <span class="icon is-small"><i class="mdi mdi-file-check"></i></span>
                  <span class="has-text-weight-semibold" :title="singleFileLinked.path">
                    {{ truncateSingleFilePath(singleFileLinked.path, 40) }}
                  </span>
                </div>
                <div class="level-right">
                  <b-button type="is-danger" size="is-small" icon-left="link-off" @click="unlinkSingleFile">
                    Unlink
                  </b-button>
                </div>
              </div>
              <p class="is-size-7 has-text-grey mt-1" v-if="singleFileLinked.duration_seconds">
                Duration: {{ formatDuration(singleFileLinked.duration_seconds) }}
              </p>
            </div>
            <div v-else class="notification is-light py-2">
              <span class="has-text-grey">No source file linked</span>
            </div>
          </div>
          <div class="column is-6">
            <h5 class="title is-6 mb-2">
              <span class="icon has-text-success"><i class="mdi mdi-file-export"></i></span>
              TX Filename (Output)
            </h5>
            <div class="notification is-light py-2">
              <span v-if="episodeInfo && episodeInfo.tx_filename" class="has-text-weight-semibold">
                {{ episodeInfo.tx_filename }}
              </span>
              <span v-else class="has-text-grey">Will be generated on processing</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Processing Workflow & Storage Selection Row -->
      <div class="columns mb-3">
        <div class="column is-half">
          <b-field label="Input Storage" label-position="on-border">
            <b-select v-model="selectedStorageConfigId" placeholder="Select Storage" size="is-small" expanded @input="onSingleFileStorageChange">
              <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }} ({{ config.bucket_name }})
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-half" v-if="processingWorkflows.length > 0">
          <b-field label="Processing Workflow" label-position="on-border">
            <b-select v-model="selectedWorkflowId" placeholder="Select workflow (optional)" size="is-small" expanded>
              <option :value="null">-- Use default settings --</option>
              <option v-for="workflow in processingWorkflows" :key="workflow.id" :value="workflow.id">
                {{ workflow.workflow_name }}
                <template v-if="workflow.is_default"> (Default)</template>
              </option>
            </b-select>
          </b-field>
        </div>
      </div>

      <!-- Inline Storage Browser Panel -->
      <div class="box storage-browser-panel">
        <div class="level mb-2">
          <div class="level-left">
            <div class="level-item">
              <h5 class="title is-6 mb-0">
                <span class="icon"><i class="mdi mdi-folder-search"></i></span>
                Browse Input Storage
              </h5>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <b-input
                v-model="singleFileBrowserSearch"
                placeholder="Filter files..."
                size="is-small"
                icon="magnify"
                style="width: 200px;"
              />
            </div>
          </div>
        </div>

        <!-- Breadcrumb Navigation -->
        <nav class="breadcrumb is-small mb-2" v-if="singleFileBrowserPath || singleFileBrowserFolders.length > 0">
          <ul>
            <li :class="{ 'is-active': !singleFileBrowserPath }">
              <a @click="browseSingleFileStorage('')">
                <span class="icon is-small"><i class="mdi mdi-folder-home"></i></span>
                <span>Root</span>
              </a>
            </li>
            <li v-for="(part, idx) in singleFilePathParts" :key="idx" :class="{ 'is-active': idx === singleFilePathParts.length - 1 }">
              <a @click="browseSingleFileStorage(singleFilePathParts.slice(0, idx + 1).join('/'))">{{ part }}</a>
            </li>
          </ul>
        </nav>

        <!-- Folders Grid -->
        <div v-if="filteredSingleFileFolders.length > 0" class="folders-section mb-3">
          <h6 class="is-size-7 has-text-weight-semibold has-text-grey mb-2">
            <span class="icon is-small"><i class="mdi mdi-folder-multiple"></i></span>
            Folders ({{ filteredSingleFileFolders.length }})
          </h6>
          <div class="folder-grid">
            <div
              v-for="folder in filteredSingleFileFolders"
              :key="folder.path"
              class="folder-item"
              @click="browseSingleFileStorage(folder.path)"
            >
              <span class="icon has-text-warning"><i class="mdi mdi-folder mdi-24px"></i></span>
              <span class="folder-name">{{ folder.name }}</span>
            </div>
          </div>
        </div>

        <!-- Files Table -->
        <div v-if="filteredSingleFileFiles.length > 0" class="files-section">
          <h6 class="is-size-7 has-text-weight-semibold has-text-grey mb-2">
            <span class="icon is-small"><i class="mdi mdi-file-multiple"></i></span>
            Files ({{ filteredSingleFileFiles.length }})
          </h6>
          <div class="file-list-container" style="max-height: 250px; overflow-y: auto;">
            <table class="table is-fullwidth is-narrow is-hoverable is-striped">
              <thead>
                <tr>
                  <th style="width: 40px;"></th>
                  <th>Filename</th>
                  <th style="width: 100px;">Size</th>
                  <th style="width: 100px;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="file in filteredSingleFileFiles" :key="file.path">
                  <td>
                    <span class="icon has-text-info"><i class="mdi mdi-file-video"></i></span>
                  </td>
                  <td>
                    <span :class="{ 'has-text-weight-semibold': isVideoFile(file.name) }">{{ file.name }}</span>
                  </td>
                  <td class="is-size-7">{{ formatFileSize(file.size) }}</td>
                  <td>
                    <b-button
                      v-if="isVideoFile(file.name)"
                      type="is-success"
                      size="is-small"
                      icon-left="link"
                      @click="linkSingleFile(file)"
                    >
                      Link
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Load More Button -->
        <div v-if="singleFileBrowserTruncated && !loadingSingleFileBrowser" class="has-text-centered py-2">
          <div class="notification is-warning is-light py-2 mb-2">
            <span class="icon is-small"><i class="mdi mdi-information"></i></span>
            <span class="is-size-7">
              This folder has more than {{ singleFileBrowserFiles.length }} files — not all are loaded yet.
              Click "Load More Files" to load the rest (the search box only filters files already loaded).
            </span>
          </div>
          <b-button type="is-primary" size="is-small" icon-left="plus" :loading="loadingMoreSingleFiles" @click="loadMoreSingleFiles">
            Load More Files
          </b-button>
        </div>

        <!-- Empty State -->
        <div v-if="!loadingSingleFileBrowser && singleFileBrowserFolders.length === 0 && singleFileBrowserFiles.length === 0" class="has-text-centered py-4">
          <span class="icon is-large has-text-grey-light"><i class="mdi mdi-folder-open-outline mdi-48px"></i></span>
          <p class="has-text-grey" v-if="selectedStorageConfigId">This folder is empty or no storage selected</p>
          <p class="has-text-grey" v-else>Select a storage configuration to browse</p>
        </div>

        <!-- Loading State -->
        <div v-if="loadingSingleFileBrowser" class="has-text-centered py-4">
          <b-loading :is-full-page="false" :active="true" />
        </div>
      </div>

      <!-- Action Buttons (when file is linked) -->
      <div v-if="singleFileLinked" class="box mt-4">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <b-tag :type="getVerificationTagType(singleFileLinked.verification_status)" size="is-medium">
                {{ getVerificationLabel(singleFileLinked.verification_status) }}
              </b-tag>
            </div>
            <div class="level-item" v-if="singleFileLinked.segmentsLinked">
              <span class="is-size-7 has-text-grey">
                Linked to {{ singleFileLinked.segmentsLinked }}/{{ singleFileLinked.totalSegments }} segments
              </span>
            </div>
          </div>
          <div class="level-right">
            <!-- Analyze button -->
            <div class="level-item" v-if="!singleFileAnalyzed">
              <b-button type="is-info" icon-left="magnify" :loading="processingSingleFile" @click="processSingleFile">
                Analyze Duration
              </b-button>
            </div>
            <!-- Approval buttons for mismatches -->
            <div class="level-item" v-if="singleFileLinked.verification_status === 'pending'">
              <div class="buttons">
                <b-button type="is-success" size="is-small" icon-left="check" @click="approveAllSegmentMismatches">
                  Approve Mismatch
                </b-button>
                <b-button type="is-danger" size="is-small" icon-left="close" @click="rejectAllSegmentMismatches">
                  Reject
                </b-button>
              </div>
            </div>
            <!-- Submit for QC button -->
            <div class="level-item" v-if="canSubmitSingleFileForQc">
              <b-button type="is-success" icon-left="cog-sync" :loading="submittingEncodeJob" @click="submitSingleFileForEncodeQc">
                Submit for Encode & QC
              </b-button>
            </div>
          </div>
        </div>

        <!-- QC Pending Status -->
        <div v-if="singleFileQcPending" class="notification is-warning is-light mt-3">
          <span class="icon"><i class="mdi mdi-clock-outline"></i></span>
          <span>File submitted for processing. QC results will appear below when complete.</span>
        </div>
      </div>
    </div>

    <!-- Multi-Segment Mode (existing functionality) -->
    <div v-else class="multi-segment-mode">
      <!-- Header with controls -->
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h3 class="subtitle is-5">
              <span class="icon"><i class="mdi mdi-link-variant"></i></span>
              Link Source Files
            </h3>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-field>
              <b-select v-model="selectedStorageConfigId" placeholder="Select Storage" @input="onStorageChange">
                <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                  {{ config.config_name }} ({{ config.bucket_name }})
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="level-item">
            <b-button
              type="is-info"
              icon-left="auto-fix"
              :loading="suggesting"
              :disabled="!selectedStorageConfigId"
              @click="autoSuggestFiles"
            >
              Auto-Suggest
            </b-button>
          </div>
          <div class="level-item">
            <b-tooltip label="Learn pattern from an example filename" position="is-bottom">
              <b-button
                type="is-link"
                icon-left="school"
                size="is-small"
                outlined
                @click="openLearnPatternModal"
              >
                Learn Pattern
              </b-button>
            </b-tooltip>
          </div>
          <div class="level-item" v-if="learnedPattern || learnedPath">
            <b-tag type="is-success" closable @close="clearLearnedPattern">
              <span v-if="learnedPattern">Pattern: {{ learnedPattern }}</span>
              <span v-if="learnedPattern && learnedPath"> | </span>
              <span v-if="learnedPath">Path: {{ learnedPath }}</span>
            </b-tag>
          </div>
        </div>
      </div>

    <!-- Workflow Selection for Multi-Segment Mode -->
    <div class="workflow-selection-section mb-4">
      <div class="columns is-multiline">
        <!-- Platform Workflow Template (determines worker type) -->
        <div class="column is-half" v-if="platformWorkflowTemplates.length > 0">
          <b-field label="Source Format" label-position="on-border" grouped class="mb-0">
            <b-select
              v-model="selectedPlatformWorkflowId"
              placeholder="Select format workflow"
              size="is-small"
              expanded
            >
              <option v-for="template in platformWorkflowTemplates" :key="template.id" :value="template.id">
                {{ template.template_name }}
                <template v-if="template.is_beta"> (Beta)</template>
              </option>
            </b-select>
            <p class="control">
              <b-tooltip label="Select based on your source file format (TS files vs MP4/MOV/MXF)" position="is-right">
                <b-icon icon="information-outline" size="is-small"></b-icon>
              </b-tooltip>
            </p>
          </b-field>
          <p v-if="selectedPlatformWorkflowId" class="help is-size-7 has-text-grey mt-1">
            {{ getPlatformWorkflowDescription(selectedPlatformWorkflowId) }}
          </p>
        </div>

        <!-- Processing Workflow (encoding preset and output destination) -->
        <div class="column is-half" v-if="processingWorkflows.length > 0">
          <b-field label="Processing Workflow" label-position="on-border" grouped class="mb-0">
            <b-select
              v-model="selectedWorkflowId"
              placeholder="Select workflow (optional)"
              size="is-small"
              expanded
            >
              <option :value="null">-- Use default settings --</option>
              <option v-for="workflow in processingWorkflows" :key="workflow.id" :value="workflow.id">
                {{ workflow.workflow_name }}
                <template v-if="workflow.is_default"> (Default)</template>
              </option>
            </b-select>
            <p class="control">
              <b-tooltip label="Workflows define encoding settings and output destination" position="is-right">
                <b-icon icon="information-outline" size="is-small"></b-icon>
              </b-tooltip>
            </p>
          </b-field>
          <p v-if="selectedWorkflowId" class="help is-size-7 has-text-grey mt-1">
            {{ getWorkflowDescription(selectedWorkflowId) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Segments Table with Source File Status -->
    <b-table
      :data="segmentsWithStatus"
      :loading="loading"
      :bordered="false"
      :striped="true"
      :hoverable="true"
      :mobile-cards="false"
      class="segments-link-table"
    >
      <template #default="props">
        <b-table-column field="segment_number" label="Seg #" width="60" numeric>
          {{ props.row.segment_number }}
        </b-table-column>

        <b-table-column field="duration" label="Expected Duration" width="120">
          {{ formatDuration(props.row.duration_seconds) }}
        </b-table-column>

        <b-table-column field="probed_duration_ms" label="Probed Duration" width="120">
          <template v-if="props.row.probed_duration_ms != null">
            <span class="has-text-info has-tooltip-arrow has-tooltip-multiline"
                  :data-tooltip="'Actual MXF source duration ffprobed during stitch on ' + formatProbedAt(props.row.probed_at)">
              {{ formatProbedSeconds(props.row.probed_duration_ms) }}
            </span>
          </template>
          <span v-else class="has-text-grey-light">-</span>
        </b-table-column>

        <b-table-column field="tx_in_ms" label="TX In" width="120">
          <template v-if="props.row.tx_in_ms != null">
            <code class="is-size-7">{{ formatMsTimecode(props.row.tx_in_ms) }}</code>
          </template>
          <span v-else class="has-text-grey-light">-</span>
        </b-table-column>

        <b-table-column field="tx_out_ms" label="TX Out" width="120">
          <template v-if="props.row.tx_out_ms != null">
            <code class="is-size-7">{{ formatMsTimecode(props.row.tx_out_ms) }}</code>
          </template>
          <span v-else class="has-text-grey-light">-</span>
        </b-table-column>

        <b-table-column field="source_filename" label="Source File">
          <template v-if="props.row.source_filename">
            <span class="has-text-success">
              <span class="icon is-small"><i class="mdi mdi-file-check"></i></span>
              {{ props.row.source_filename }}
            </span>
          </template>
          <template v-else>
            <span class="has-text-grey-light">
              <span class="icon is-small"><i class="mdi mdi-file-question"></i></span>
              Not linked
            </span>
          </template>
        </b-table-column>

        <b-table-column field="verification_status" label="Verification" width="140">
          <b-tag :type="getVerificationTagType(props.row.verification_status)">
            {{ getVerificationLabel(props.row.verification_status) }}
          </b-tag>
        </b-table-column>

        <b-table-column field="duration_difference" label="Duration Diff" width="120">
          <template v-if="props.row.duration_difference_seconds !== null">
            <span :class="getDifferenceClass(props.row.duration_difference_seconds)">
              {{ formatDifference(props.row.duration_difference_seconds) }}
            </span>
          </template>
          <template v-else>
            <span class="has-text-grey-light">-</span>
          </template>
        </b-table-column>

        <b-table-column label="Actions" width="200">
          <div class="buttons are-small">
            <b-button
              v-if="!props.row.source_filename"
              type="is-primary"
              icon-left="file-search"
              size="is-small"
              @click="openFileBrowser(props.row)"
            >
              Browse
            </b-button>
            <template v-if="props.row.verification_status === 'pending'">
              <b-button
                type="is-success"
                icon-left="check"
                size="is-small"
                @click="approveMismatch(props.row)"
              >
                Approve
              </b-button>
              <b-button
                type="is-danger"
                icon-left="close"
                size="is-small"
                @click="rejectMismatch(props.row)"
              >
                Reject
              </b-button>
            </template>
            <b-button
              v-if="props.row.source_filename"
              type="is-light"
              icon-left="link-off"
              size="is-small"
              @click="unlinkFile(props.row)"
            >
              Unlink
            </b-button>
          </div>
        </b-table-column>
      </template>

      <template #empty>
        <div class="has-text-centered">
          <p>No segments found for this episode</p>
        </div>
      </template>
    </b-table>

    <!-- Stitch Actions -->
    <div v-if="segmentsWithStatus.length > 0" class="stitch-actions has-margin-top-20">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="field is-grouped is-grouped-multiline">
              <div class="control">
                <div class="tags has-addons">
                  <span class="tag">Total</span>
                  <span class="tag is-dark">{{ summary.total }}</span>
                </div>
              </div>
              <div class="control">
                <div class="tags has-addons">
                  <span class="tag">Linked</span>
                  <span class="tag is-info">{{ summary.linked }}</span>
                </div>
              </div>
              <div class="control">
                <div class="tags has-addons">
                  <span class="tag">Verified</span>
                  <span class="tag is-success">{{ summary.verified }}</span>
                </div>
              </div>
              <div class="control" v-if="summary.pending > 0">
                <div class="tags has-addons">
                  <span class="tag">Pending</span>
                  <span class="tag is-warning">{{ summary.pending }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button
              type="is-light"
              icon-left="refresh"
              :loading="recalculatingTimecodes"
              @click="recalculateTimecodes"
              title="Recalculate all segment in/out points based on actual durations and gap settings"
            >
              Recalculate Timecodes
            </b-button>
          </div>
          <div class="level-item">
            <b-button
              type="is-success"
              icon-left="merge"
              :disabled="!canStitch"
              :loading="submittingJob"
              @click="submitStitchJob"
            >
              Submit Stitch Job
            </b-button>
          </div>
        </div>
      </div>
    </div>
    </div><!-- End of multi-segment-mode -->

    <!-- QC Status Panel (shown for both single file and multi-segment modes) -->
    <div v-if="qcStatus" class="qc-status-panel has-margin-top-20">
      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h4 class="subtitle is-6 mb-0">
                <span class="icon"><i class="mdi mdi-check-decagram"></i></span>
                {{ effectiveSourceType === 'single' ? 'QC Status' : 'Stitch QC Status' }}
              </h4>
            </div>
            <div class="level-item">
              <b-tag :type="getQcStatusType(qcStatus.qc.status)" size="is-medium">
                {{ getQcStatusLabel(qcStatus.qc.status) }}
              </b-tag>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item" v-if="qcStatus.qc.reportPath || qcStatus.qc.resultId || qcStatus.qc.status">
              <b-dropdown aria-role="list" position="is-bottom-left">
                <template #trigger>
                  <b-button
                    type="is-info"
                    size="is-small"
                    icon-left="file-pdf-box"
                    icon-right="menu-down"
                    :loading="loadingQcReport"
                  >
                    QC Report
                  </b-button>
                </template>
                <b-dropdown-item aria-role="listitem" @click="openQcReport">
                  <span class="icon"><i class="mdi mdi-eye"></i></span>
                  <span>View in Browser</span>
                </b-dropdown-item>
                <b-dropdown-item aria-role="listitem" @click="downloadQcReport">
                  <span class="icon"><i class="mdi mdi-download"></i></span>
                  <span>Download PDF</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>

        <!-- Encoding Error Message (shown when stitch failed before QC) -->
        <div v-if="qcStatus.stitch && qcStatus.stitch.status === 'failed' && qcStatus.stitch.errorMessage" class="notification is-danger mt-3">
          <span class="icon"><i class="mdi mdi-alert-circle"></i></span>
          <span>
            <strong>Encoding Failed:</strong>
            {{ qcStatus.stitch.errorMessage }}
          </span>
        </div>

        <!-- QC Summary Stats -->
        <div class="columns is-multiline mt-3" v-if="qcStatus.qc.status">
          <div class="column is-3">
            <div class="has-text-centered">
              <p class="heading">Errors</p>
              <p class="title is-4" :class="qcStatus.qc.errorCount > 0 ? 'has-text-danger' : 'has-text-success'">
                {{ qcStatus.qc.errorCount }}
              </p>
            </div>
          </div>
          <div class="column is-3">
            <div class="has-text-centered">
              <p class="heading">Warnings</p>
              <p class="title is-4" :class="qcStatus.qc.warningCount > 0 ? 'has-text-warning' : 'has-text-success'">
                {{ qcStatus.qc.warningCount }}
              </p>
            </div>
          </div>
          <div class="column is-3">
            <div class="has-text-centered">
              <p class="heading">Loudness</p>
              <p class="title is-5" :class="qcStatus.qc.loudness.compliant ? 'has-text-success' : 'has-text-danger'">
                {{ qcStatus.qc.loudness.integrated ? qcStatus.qc.loudness.integrated.toFixed(1) + ' LUFS' : 'N/A' }}
              </p>
            </div>
          </div>
          <div class="column is-3">
            <div class="has-text-centered">
              <p class="heading">Duration Match</p>
              <p class="title is-5">
                <span class="icon" :class="qcStatus.qc.duration.match ? 'has-text-success' : 'has-text-danger'">
                  <i :class="qcStatus.qc.duration.match ? 'mdi mdi-check-circle' : 'mdi mdi-close-circle'"></i>
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- QC Issues List -->
        <div v-if="qcStatus.qc.issues && qcStatus.qc.issues.length > 0" class="mt-4">
          <h5 class="is-size-7 has-text-weight-semibold mb-2">Issues Found:</h5>
          <div class="qc-issues-list">
            <div
              v-for="(issue, idx) in qcStatus.qc.issues.slice(0, 5)"
              :key="idx"
              class="notification is-small"
              :class="issue.severity === 'error' ? 'is-danger' : 'is-warning'"
            >
              <span class="icon is-small">
                <i :class="issue.severity === 'error' ? 'mdi mdi-alert-circle' : 'mdi mdi-alert'"></i>
              </span>
              <span>{{ issue.description }}</span>
              <span v-if="issue.timecode" class="has-text-grey ml-2">({{ issue.timecode }})</span>
            </div>
            <p v-if="qcStatus.qc.issues.length > 5" class="has-text-grey is-size-7">
              ...and {{ qcStatus.qc.issues.length - 5 }} more issues (see full report)
            </p>
          </div>
        </div>

        <!-- Detailed Detection Timecodes -->
        <div v-if="hasDetailedDetections" class="mt-4">
          <!-- Black Frames Section -->
          <div v-if="qcStatus.qc.alerts.blackFrameDetails && qcStatus.qc.alerts.blackFrameDetails.length > 0" class="mb-4">
            <h5 class="is-size-7 has-text-weight-semibold mb-2">
              <span class="icon is-small has-text-dark"><i class="mdi mdi-square"></i></span>
              Black Frames Detected ({{ qcStatus.qc.alerts.blackFrameDetails.length }})
            </h5>
            <div class="detection-list">
              <div
                v-for="(frame, idx) in qcStatus.qc.alerts.blackFrameDetails.slice(0, 10)"
                :key="'bf-' + idx"
                class="detection-item"
              >
                <span class="has-text-weight-medium">{{ formatTimecode(frame.start_s) }}</span>
                <span class="has-text-grey mx-1">→</span>
                <span class="has-text-weight-medium">{{ formatTimecode(frame.end_s) }}</span>
                <span class="has-text-grey ml-2">({{ formatDuration(frame.duration_s) }})</span>
              </div>
              <p v-if="qcStatus.qc.alerts.blackFrameDetails.length > 10" class="has-text-grey is-size-7 mt-1">
                ...and {{ qcStatus.qc.alerts.blackFrameDetails.length - 10 }} more (see PDF report)
              </p>
            </div>
          </div>

          <!-- Silence Periods Section -->
          <div v-if="qcStatus.qc.alerts.silencePeriodDetails && qcStatus.qc.alerts.silencePeriodDetails.length > 0" class="mb-4">
            <h5 class="is-size-7 has-text-weight-semibold mb-2">
              <span class="icon is-small has-text-danger"><i class="mdi mdi-volume-off"></i></span>
              Silence Periods Detected ({{ qcStatus.qc.alerts.silencePeriodDetails.length }})
            </h5>
            <div class="detection-list">
              <div
                v-for="(silence, idx) in qcStatus.qc.alerts.silencePeriodDetails.slice(0, 10)"
                :key="'sp-' + idx"
                class="detection-item"
              >
                <span class="has-text-weight-medium">{{ formatTimecode(silence.start_s) }}</span>
                <span class="has-text-grey mx-1">→</span>
                <span class="has-text-weight-medium">{{ formatTimecode(silence.end_s) }}</span>
                <span class="has-text-grey ml-2">({{ formatDuration(silence.duration_s) }})</span>
              </div>
              <p v-if="qcStatus.qc.alerts.silencePeriodDetails.length > 10" class="has-text-grey is-size-7 mt-1">
                ...and {{ qcStatus.qc.alerts.silencePeriodDetails.length - 10 }} more (see PDF report)
              </p>
            </div>
          </div>

          <!-- Freeze Frames Section -->
          <div v-if="qcStatus.qc.alerts.freezeFrameDetails && qcStatus.qc.alerts.freezeFrameDetails.length > 0" class="mb-4">
            <h5 class="is-size-7 has-text-weight-semibold mb-2">
              <span class="icon is-small has-text-info"><i class="mdi mdi-pause-circle"></i></span>
              Freeze Frames Detected ({{ qcStatus.qc.alerts.freezeFrameDetails.length }})
            </h5>
            <div class="detection-list">
              <div
                v-for="(freeze, idx) in qcStatus.qc.alerts.freezeFrameDetails.slice(0, 10)"
                :key="'ff-' + idx"
                class="detection-item"
              >
                <span class="has-text-weight-medium">{{ formatTimecode(freeze.start_s) }}</span>
                <span class="has-text-grey mx-1">→</span>
                <span class="has-text-weight-medium">{{ formatTimecode(freeze.end_s) }}</span>
                <span class="has-text-grey ml-2">({{ formatDuration(freeze.duration_s) }})</span>
              </div>
              <p v-if="qcStatus.qc.alerts.freezeFrameDetails.length > 10" class="has-text-grey is-size-7 mt-1">
                ...and {{ qcStatus.qc.alerts.freezeFrameDetails.length - 10 }} more (see PDF report)
              </p>
            </div>
          </div>
        </div>

        <!-- Override info if already overridden -->
        <div v-if="qcStatus.override" class="notification is-info is-light mt-4">
          <span class="icon"><i class="mdi mdi-check-decagram"></i></span>
          <span>
            QC overridden by <strong>{{ qcStatus.override.by }}</strong>
            <span v-if="qcStatus.override.reason">: "{{ qcStatus.override.reason }}"</span>
          </span>
        </div>

        <!-- Override/Reject Actions -->
        <div v-if="qcStatus.canOverride" class="buttons mt-4">
          <b-button
            type="is-warning"
            icon-left="check-decagram"
            @click="showQcOverrideModal = true"
          >
            Override QC
          </b-button>
          <b-button
            type="is-danger"
            icon-left="close-circle"
            @click="showQcRejectModal = true"
          >
            {{ effectiveSourceType === 'single' ? 'Reject & Re-process' : 'Reject & Re-stitch' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- QC Override Modal -->
    <b-modal :active.sync="showQcOverrideModal" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Override QC Failure</p>
            <button type="button" class="delete" @click="showQcOverrideModal = false" />
          </header>
          <section class="modal-card-body">
            <div class="notification is-warning">
              <p><strong>Warning:</strong> You are about to approve a file that failed quality control checks.</p>
              <p class="mt-2">This action will be logged for audit purposes. Please provide a detailed reason.</p>
            </div>
            <b-field label="Reason for Override *" message="Minimum 10 characters required">
              <b-input
                v-model="qcOverrideReason"
                type="textarea"
                placeholder="Explain why this QC failure can be ignored (e.g., 'Minor audio level variance acceptable for this broadcast window')"
                rows="3"
              />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showQcOverrideModal = false">Cancel</b-button>
            <b-button
              type="is-warning"
              icon-left="check-decagram"
              :disabled="qcOverrideReason.length < 10"
              :loading="processingQcAction"
              @click="submitQcOverride"
            >
              Confirm Override
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- QC Reject Modal -->
    <b-modal :active.sync="showQcRejectModal" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Reject & Request Re-stitch</p>
            <button type="button" class="delete" @click="showQcRejectModal = false" />
          </header>
          <section class="modal-card-body">
            <div class="notification is-danger is-light">
              <p><strong>This will:</strong></p>
              <ul class="mt-2">
                <li>Mark the current stitch as failed</li>
                <li>Allow you to re-link source files if needed</li>
                <li>Enable submitting a new stitch job</li>
              </ul>
            </div>
            <b-field label="Reason for Rejection (optional)">
              <b-input
                v-model="qcRejectReason"
                type="textarea"
                placeholder="Optional: Describe why this file is being rejected"
                rows="2"
              />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showQcRejectModal = false">Cancel</b-button>
            <b-button
              type="is-danger"
              icon-left="close-circle"
              :loading="processingQcAction"
              @click="submitQcReject"
            >
              Reject Stitch
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- Learn Pattern Modal -->
    <b-modal :active.sync="showLearnPatternModal" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card" style="width: 600px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-school"></i></span>
              Learn Pattern from Example
            </p>
            <button type="button" class="delete" @click="showLearnPatternModal = false" />
          </header>
          <section class="modal-card-body">
            <div class="notification is-info is-light">
              <p>Paste an example filename from your source files. The system will analyze it to learn the naming pattern for this series.</p>
            </div>

            <b-field label="Example Filename *" message="Include the full filename without the folder path">
              <b-input
                v-model="exampleFilename"
                placeholder="e.g., ReaTsotella_Season25_E001_Seg1_08M24S.ts"
                expanded
              />
            </b-field>

            <b-field label="Search Folder Path (optional)" message="Limit auto-suggest to files in this subfolder (relative to storage config base folder)">
              <b-input
                v-model="learnedPath"
                placeholder="e.g., ReaTsotella/Season25 or leave empty to search all folders"
                expanded
              />
            </b-field>

            <div class="columns">
              <div class="column is-half">
                <b-field label="Episode Number (for this example)">
                  <b-numberinput
                    v-model="exampleEpisodeNumber"
                    :min="1"
                    :max="9999"
                    controls-position="compact"
                  />
                </b-field>
              </div>
              <div class="column is-half">
                <b-field label="Segment Number (for this example)">
                  <b-numberinput
                    v-model="exampleSegmentNumber"
                    :min="1"
                    :max="99"
                    controls-position="compact"
                  />
                </b-field>
              </div>
            </div>

            <!-- Show learned pattern result -->
            <div v-if="learnedPattern" class="mt-4">
              <div class="notification" :class="learnedPatternConfidence >= 0.75 ? 'is-success' : learnedPatternConfidence >= 0.5 ? 'is-warning' : 'is-danger'" style="background-color: inherit;">
                <p class="has-text-weight-bold">Learned Pattern:</p>
                <code class="is-size-5">{{ learnedPattern }}</code>
                <p class="mt-2 is-size-7">
                  Confidence: {{ Math.round(learnedPatternConfidence * 100) }}%
                  <span v-if="learnedPatternMatchedParts">
                    | Matched: {{ Object.keys(learnedPatternMatchedParts).join(', ') }}
                  </span>
                </p>
              </div>

              <div class="content is-small mt-3">
                <p><strong>Placeholders found:</strong></p>
                <ul>
                  <li v-if="learnedPattern.includes('{SHOW}')"><code>{SHOW}</code> - Show title</li>
                  <li v-if="learnedPattern.includes('{S}') || learnedPattern.includes('{SS}')"><code>{SS}</code> - Season number</li>
                  <li v-if="learnedPattern.includes('{E}') || learnedPattern.includes('{EE}')"><code>{EEE}</code> - Episode number</li>
                  <li v-if="learnedPattern.includes('{N}')"><code>{N}</code> - Segment number</li>
                  <li v-if="learnedPattern.includes('{*}')"><code>{*}</code> - Variable content (duration, ID, etc.)</li>
                </ul>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showLearnPatternModal = false">Cancel</b-button>
            <b-button
              type="is-info"
              icon-left="magnify"
              :loading="learningPattern"
              :disabled="!exampleFilename"
              @click="learnPattern"
            >
              Analyze Pattern
            </b-button>
            <b-button
              v-if="learnedPattern && learnedPatternConfidence >= 0.5"
              type="is-success"
              icon-left="check"
              @click="applyLearnedPattern"
            >
              Use This Pattern
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- File Browser Modal -->
    <b-modal
      :active.sync="showFileBrowser"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'x', 'outside']"
      width="900px"
      @close="browsingForSingleFile = false"
    >
      <template #default>
        <div class="modal-card" style="width: 900px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <template v-if="browsingForSingleFile">
                Browse Storage - Select Complete Source File (all segments)
              </template>
              <template v-else>
                Browse Storage - Segment {{ currentSegment ? currentSegment.segment_number : '' }}
              </template>
            </p>
            <button type="button" class="delete" @click="showFileBrowser = false; browsingForSingleFile = false" />
          </header>
          <section class="modal-card-body">
            <!-- Breadcrumb with Go Up button -->
            <div class="level mb-3">
              <div class="level-left">
                <div class="level-item">
                  <nav class="breadcrumb is-small mb-0" aria-label="breadcrumbs">
                    <ul>
                      <li :class="{ 'is-active': currentPath === '' }">
                        <a @click="browseTo('')">
                          <span class="icon is-small"><i class="mdi mdi-folder-home"></i></span>
                          <span>Root</span>
                        </a>
                      </li>
                      <li v-for="(part, index) in pathParts" :key="index" :class="{ 'is-active': index === pathParts.length - 1 }">
                        <a @click="browseTo(pathParts.slice(0, index + 1).join('/'))">{{ part }}</a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
              <div class="level-right">
                <div class="level-item" v-if="currentPath">
                  <b-button
                    type="is-light"
                    icon-left="arrow-up"
                    size="is-small"
                    @click="goUpOneLevel"
                  >
                    Up
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Current storage info -->
            <div v-if="selectedStorageConfig" class="notification is-light is-small py-2 mb-3">
              <span class="icon is-small"><i class="mdi mdi-information"></i></span>
              <span class="is-size-7">
                Storage: <strong>{{ selectedStorageConfig.config_name }}</strong>
                <span v-if="selectedStorageConfig.base_folder"> (base: {{ selectedStorageConfig.base_folder }})</span>
              </span>
            </div>

            <!-- Manual path entry -->
            <b-field grouped>
              <b-input
                v-model="manualPath"
                placeholder="Enter folder path (e.g., ShowName/Season01)"
                size="is-small"
                expanded
                @keyup.enter="browseToManualPath"
              />
              <p class="control">
                <b-button type="is-primary" size="is-small" icon-left="folder-search" @click="browseToManualPath">
                  Go to Path
                </b-button>
              </p>
            </b-field>

            <!-- Search -->
            <b-field>
              <b-input
                v-model="searchTerm"
                placeholder="Search files..."
                icon="magnify"
                @keyup.enter="searchFiles"
              />
              <p class="control">
                <b-button type="is-info" @click="searchFiles">Search</b-button>
              </p>
            </b-field>

            <!-- Folders Section (shown prominently first) -->
            <div v-if="browseFolders.length > 0" class="folders-section mb-4">
              <h6 class="is-size-7 has-text-weight-semibold has-text-grey mb-2">
                <span class="icon is-small"><i class="mdi mdi-folder-multiple"></i></span>
                Folders ({{ browseFolders.length }}) - Click to navigate
              </h6>
              <div class="folder-grid">
                <div
                  v-for="folder in browseFolders"
                  :key="folder.path"
                  class="folder-item"
                  @click="browseTo(folder.path)"
                >
                  <span class="icon has-text-warning"><i class="mdi mdi-folder mdi-24px"></i></span>
                  <span class="folder-name">{{ folder.name }}</span>
                </div>
              </div>
            </div>

            <!-- No folders message -->
            <div v-else-if="!browsing && browseResults.length > 0" class="notification is-info is-light is-small py-2 mb-3">
              <span class="icon is-small"><i class="mdi mdi-information"></i></span>
              <span class="is-size-7">No subfolders in this location. Showing files only.</span>
            </div>

            <!-- Files Section -->
            <div v-if="browseFiles.length > 0" class="files-section">
              <h6 class="is-size-7 has-text-weight-semibold has-text-grey mb-2">
                <span class="icon is-small"><i class="mdi mdi-file-multiple"></i></span>
                Files ({{ browseFiles.length }})
              </h6>
              <div class="file-browser-list" style="max-height: 300px; overflow-y: auto;">
                <b-table
                  :data="browseFiles"
                  :loading="browsing"
                  :hoverable="true"
                  :striped="true"
                  :narrowed="true"
                >
                  <template #default="props">
                    <b-table-column field="type" label="" width="40">
                      <span class="icon">
                        <i class="mdi mdi-file-video"></i>
                      </span>
                    </b-table-column>
                    <b-table-column field="name" label="Name">
                      <span :class="{ 'has-text-weight-bold': isVideoFile(props.row.name) }">
                        {{ props.row.name }}
                      </span>
                    </b-table-column>
                    <b-table-column field="size" label="Size" width="100">
                      {{ props.row.size ? formatFileSize(props.row.size) : '-' }}
                    </b-table-column>
                    <b-table-column label="" width="160">
                      <div class="buttons are-small">
                        <b-button
                          v-if="isVideoFile(props.row.name)"
                          type="is-light"
                          size="is-small"
                          icon-left="play"
                          title="Preview video"
                          @click="previewFile(props.row)"
                        >
                          Preview
                        </b-button>
                        <b-button
                          v-if="isVideoFile(props.row.name)"
                          type="is-success"
                          size="is-small"
                          icon-left="link"
                          @click="selectFile(props.row)"
                        >
                          Link
                        </b-button>
                      </div>
                    </b-table-column>
                  </template>
                </b-table>
              </div>
            </div>

            <!-- Load More Button (when results are truncated) -->
            <div v-if="isTruncated && !browsing" class="has-text-centered py-3">
              <div class="notification is-info is-light py-2 mb-3">
                <span class="icon is-small"><i class="mdi mdi-information"></i></span>
                <span class="is-size-7">
                  Showing {{ totalFoldersLoaded }} folders and {{ totalFilesLoaded }} files. More items available.
                </span>
              </div>
              <b-button
                type="is-primary"
                icon-left="plus"
                :loading="loadingMore"
                @click="loadMoreResults"
              >
                Load More Items
              </b-button>
            </div>

            <!-- Items loaded count (when all loaded) -->
            <div v-else-if="!browsing && browseResults.length > 0 && !isTruncated" class="has-text-centered py-2">
              <span class="is-size-7 has-text-grey">
                Showing all {{ totalFoldersLoaded }} folders and {{ totalFilesLoaded }} files
              </span>
            </div>

            <!-- Empty state -->
            <div v-if="!browsing && !loadingMore && browseResults.length === 0" class="has-text-centered py-4">
              <span class="icon is-large has-text-grey-light">
                <i class="mdi mdi-folder-open-outline mdi-48px"></i>
              </span>
              <p class="has-text-grey">This folder is empty</p>
            </div>

            <!-- Loading state -->
            <div v-if="browsing" class="has-text-centered py-4">
              <b-loading :is-full-page="false" :active="true" />
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showFileBrowser = false; browsingForSingleFile = false">Cancel</b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- Video Preview Modal -->
    <b-modal
      :active.sync="showVideoPreview"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'x', 'outside']"
      width="960px"
      @close="closeVideoPreview"
    >
      <template #default>
        <div class="modal-card" style="width: 960px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-play-circle"></i></span>
              {{ previewFilename }}
            </p>
            <button type="button" class="delete" @click="closeVideoPreview" />
          </header>
          <section class="modal-card-body video-preview-body">
            <div v-if="loadingPreview" class="has-text-centered py-6">
              <b-loading :is-full-page="false" :active="true" />
              <p class="mt-4">Loading video preview...</p>
            </div>
            <div v-else-if="previewError" class="has-text-centered py-6">
              <span class="icon is-large has-text-danger">
                <i class="mdi mdi-alert-circle mdi-48px"></i>
              </span>
              <p class="has-text-danger">{{ previewError }}</p>
            </div>
            <div v-else-if="previewUrl" class="video-container">
              <video
                ref="videoPlayer"
                :src="previewUrl"
                controls
                class="video-player"
                @error="handleVideoError"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="closeVideoPreview">Close</b-button>
            <b-button
              v-if="previewFile && currentSegment"
              type="is-success"
              icon-left="link"
              @click="selectFileFromPreview"
            >
              Link This File
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import storageBrowseApi from '@/api/storageBrowse'
import stitchJobsApi from '@/api/stitchJobs'
import segmentsApi from '@/api/segments'
import episodesApi from '@/api/episodes'
import processingWorkflowsApi from '@/api/processingWorkflows'

export default {
  name: 'SegmentSourceLinker',
  props: {
    episodeId: {
      type: [Number, String],
      required: true
    },
    // Optional: Pass show title and season for pattern learning
    // If not provided, will be fetched from episode data
    showTitleProp: {
      type: String,
      default: null
    },
    seasonNumberProp: {
      type: [Number, String],
      default: null
    },
    // When true, hides the internal source type toggle (parent controls it)
    hideTypeToggle: {
      type: Boolean,
      default: false
    },
    // Source type controlled by parent ('single' or 'multi')
    sourceTypeProp: {
      type: String,
      default: null,
      validator: v => v === null || ['single', 'multi'].includes(v)
    }
  },
  data () {
    return {
      loading: false,
      suggesting: false,
      browsing: false,
      submittingJob: false,
      storageConfigs: [],
      selectedStorageConfigId: null,
      segments: [],
      showFileBrowser: false,
      currentSegment: null,
      currentPath: '',
      manualPath: '',
      searchTerm: '',
      browseResults: [],
      // Video preview state
      showVideoPreview: false,
      previewUrl: null,
      previewFilename: '',
      previewFileData: null,
      loadingPreview: false,
      previewError: null,
      // Pagination state for folder browser
      nextContinuationToken: null,
      isTruncated: false,
      loadingMore: false,
      totalFoldersLoaded: 0,
      totalFilesLoaded: 0,
      // Timecode recalculation state
      recalculatingTimecodes: false,
      // QC status state
      qcStatus: null,
      loadingQcReport: false,
      showQcOverrideModal: false,
      showQcRejectModal: false,
      qcOverrideReason: '',
      qcRejectReason: '',
      processingQcAction: false,
      // Single file mode state
      episodeSourceType: 'multi', // 'single' or 'multi'
      processingSingleFile: false,
      browsingForSingleFile: false,
      submittingEncodeJob: false,
      // Inline storage browser for single file mode
      singleFileBrowserPath: '',
      singleFileBrowserFolders: [],
      singleFileBrowserFiles: [],
      singleFileBrowserSearch: '',
      singleFileBrowserToken: null,
      singleFileBrowserTruncated: false,
      loadingSingleFileBrowser: false,
      loadingMoreSingleFiles: false,
      // Processing workflow state (org-level workflows)
      processingWorkflows: [],
      selectedWorkflowId: null,
      loadingWorkflows: false,
      // Platform workflow templates (determines worker type: ts_library_to_tx vs format_convert)
      platformWorkflowTemplates: [],
      selectedPlatformWorkflowId: null,
      loadingPlatformWorkflows: false,
      // Learn pattern from example state
      showLearnPatternModal: false,
      learningPattern: false,
      exampleFilename: '',
      exampleEpisodeNumber: null,
      exampleSegmentNumber: 1,
      learnedPattern: null,
      learnedPatternConfidence: 0,
      learnedPatternMatchedParts: null,
      // Optional subfolder path for auto-suggest (set via Learn Pattern modal)
      learnedPath: '',
      // Episode/media info for pattern learning (fetched if not provided via props)
      episodeInfo: null
    }
  },
  computed: {
    // Show title for pattern learning (from props or fetched episode info)
    showTitle () {
      if (this.showTitleProp) return this.showTitleProp
      if (this.episodeInfo?.show_title) return this.episodeInfo.show_title
      if (this.episodeInfo?.media_title) return this.episodeInfo.media_title
      if (this.episodeInfo?.title) return this.episodeInfo.title
      return ''
    },
    // Season number for pattern learning (from props or fetched episode info)
    seasonNumber () {
      if (this.seasonNumberProp !== null) return this.seasonNumberProp
      if (this.episodeInfo?.series_no) return this.episodeInfo.series_no
      if (this.episodeInfo?.season_number) return this.episodeInfo.season_number
      return null
    },
    // Effective source type - uses prop if provided, otherwise internal state
    effectiveSourceType () {
      return this.sourceTypeProp || this.episodeSourceType
    },
    segmentsWithStatus () {
      // Inject cumulative TX in/out points based on probed_duration_ms.
      // Stitch worker ffprobes each MXF segment and writes probed_duration_ms +
      // probed_at to the segments table on success. Cumulative timecodes here are
      // the soft-segmenting in/out points within the stitched TX file:
      //   Seg 1: 0 → probed_1
      //   Seg 2: probed_1 → probed_1 + probed_2
      //   ...
      // Null until first successful stitch run for that episode.
      const sorted = [...this.segments].sort((a, b) =>
        (a.segment_number || 0) - (b.segment_number || 0)
      )
      let cursorMs = 0
      const cumulative = new Map()
      for (const s of sorted) {
        if (s.probed_duration_ms != null) {
          const inMs = cursorMs
          cursorMs += Number(s.probed_duration_ms)
          cumulative.set(s.id, { tx_in_ms: inMs, tx_out_ms: cursorMs })
        }
      }
      return this.segments.map(s => ({
        ...s,
        verification_status: s.verification_status || 'not_linked',
        tx_in_ms: cumulative.get(s.id)?.tx_in_ms ?? null,
        tx_out_ms: cumulative.get(s.id)?.tx_out_ms ?? null
      }))
    },
    pathParts () {
      if (!this.currentPath) return []
      return this.currentPath.split('/').filter(p => p)
    },
    summary () {
      const total = this.segments.length
      const linked = this.segments.filter(s => s.source_filename).length
      const verified = this.segments.filter(s =>
        ['matched', 'mismatch_approved'].includes(s.verification_status)
      ).length
      const pending = this.segments.filter(s => s.verification_status === 'pending').length
      return { total, linked, verified, pending }
    },
    canStitch () {
      // Only allow stitching when in multi-segment mode with multiple segments
      // All segments must be verified/approved
      return this.effectiveSourceType === 'multi' &&
        this.summary.total > 1 &&
        this.summary.verified === this.summary.total &&
        this.summary.pending === 0
    },
    // Check if single file can be processed (all segments linked to same file)
    canProcessSingleFile () {
      if (!this.singleFileLinked) return false
      // Can process if verification passed or approved
      return ['matched', 'mismatch_approved'].includes(this.singleFileLinked.verification_status)
    },
    // Get the single file linked data - check if ALL segments are linked to the same file
    singleFileLinked () {
      if (this.effectiveSourceType !== 'single' || this.segments.length === 0) {
        return null
      }
      // Check if any segment has a source file
      const linkedSegments = this.segments.filter(s => s.source_filename)
      if (linkedSegments.length === 0) {
        return null
      }
      // Get the first linked segment's file info
      const firstLinked = linkedSegments[0]
      // Count how many segments are linked (in single file mode, should be all)
      return {
        filename: firstLinked.source_filename,
        path: firstLinked.source_path,
        duration_seconds: firstLinked.source_duration_seconds,
        verification_status: this.getOverallSingleFileStatus(),
        segmentsLinked: linkedSegments.length,
        totalSegments: this.segments.length
      }
    },
    // Check if single file has been analyzed (has duration data)
    singleFileAnalyzed () {
      if (!this.singleFileLinked) return false
      return this.singleFileLinked.duration_seconds && this.singleFileLinked.duration_seconds > 0
    },
    // Check if single file can be submitted for encode & QC
    canSubmitSingleFileForQc () {
      if (this.effectiveSourceType !== 'single') return false
      if (!this.singleFileLinked) return false
      // Must be linked and verified
      if (!['matched', 'mismatch_approved'].includes(this.singleFileLinked.verification_status)) return false
      // Must not already have a pending/processing QC
      if (this.qcStatus && ['pending', 'processing'].includes(this.qcStatus.qc?.status)) return false
      // Must not already be completed successfully
      if (this.qcStatus && ['passed', 'overridden'].includes(this.qcStatus.qc?.status)) return false
      return true
    },
    // Check if single file QC is pending/processing
    singleFileQcPending () {
      if (this.effectiveSourceType !== 'single') return false
      return this.qcStatus && ['pending', 'processing'].includes(this.qcStatus.qc?.status)
    },
    // Check if QC status has detailed detection data (black frames, silence, freeze frames)
    hasDetailedDetections () {
      if (!this.qcStatus || !this.qcStatus.qc || !this.qcStatus.qc.alerts) return false
      const alerts = this.qcStatus.qc.alerts
      return (
        (alerts.blackFrameDetails && alerts.blackFrameDetails.length > 0) ||
        (alerts.silencePeriodDetails && alerts.silencePeriodDetails.length > 0) ||
        (alerts.freezeFrameDetails && alerts.freezeFrameDetails.length > 0)
      )
    },
    // Get selected storage config object
    selectedStorageConfig () {
      if (!this.selectedStorageConfigId) return null
      return this.storageConfigs.find(c => c.id === this.selectedStorageConfigId)
    },
    // Separate folders from browse results
    browseFolders () {
      return this.browseResults.filter(item => item.type === 'folder')
    },
    // Separate files from browse results
    browseFiles () {
      return this.browseResults.filter(item => item.type === 'file')
    },
    // Single file browser path parts for breadcrumb
    singleFilePathParts () {
      if (!this.singleFileBrowserPath) return []
      return this.singleFileBrowserPath.split('/').filter(p => p)
    },
    // Filtered folders for single file browser
    filteredSingleFileFolders () {
      if (!this.singleFileBrowserSearch) return this.singleFileBrowserFolders
      const search = this.singleFileBrowserSearch.toLowerCase()
      return this.singleFileBrowserFolders.filter(f => f.name.toLowerCase().includes(search))
    },
    // Filtered files for single file browser
    filteredSingleFileFiles () {
      let files = this.singleFileBrowserFiles
      if (this.singleFileBrowserSearch) {
        const search = this.singleFileBrowserSearch.toLowerCase()
        files = files.filter(f => f.name.toLowerCase().includes(search))
      }
      return files
    }
  },
  watch: {
    episodeId: {
      immediate: true,
      handler (val) {
        if (val) {
          this.loadData()
        }
      }
    },
    // Watch effectiveSourceType to load browser when switching to single mode
    effectiveSourceType: {
      handler (newType) {
        if (newType === 'single' && this.selectedStorageConfigId) {
          this.$nextTick(() => {
            this.browseSingleFileStorage('')
          })
        }
      }
    }
  },
  mounted () {
    this.loadStorageConfigs()
    this.loadProcessingWorkflows()
    this.loadPlatformWorkflowTemplates()
  },
  methods: {
    // Format seconds to timecode (HH:MM:SS:FF at 25fps)
    formatTimecode (seconds) {
      if (seconds === null || seconds === undefined) return '00:00:00:00'
      const totalSeconds = Math.abs(seconds)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const secs = Math.floor(totalSeconds % 60)
      const frames = Math.round((totalSeconds % 1) * 25)
      return [
        String(hours).padStart(2, '0'),
        String(minutes).padStart(2, '0'),
        String(secs).padStart(2, '0'),
        String(frames).padStart(2, '0')
      ].join(':')
    },

    // Format duration in seconds to human readable (e.g., "2.5s" or "1m 30s")
    formatDuration (seconds) {
      if (seconds === null || seconds === undefined) return '0s'
      if (seconds < 60) {
        return `${seconds.toFixed(1)}s`
      }
      const mins = Math.floor(seconds / 60)
      const secs = Math.round(seconds % 60)
      return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
    },

    // Format milliseconds as HH:MM:SS.mmm — used for soft-segmenting TX in/out
    // points. We don't use HH:MM:SS:FF here because the source MXF segments may
    // be at different frame rates and probed_duration_ms is the only honest
    // unit. Frame-accurate display can be derived from this if needed.
    formatMsTimecode (ms) {
      if (ms === null || ms === undefined) return '-'
      const total = Math.max(0, Math.round(Number(ms)))
      const h = Math.floor(total / 3600000)
      const m = Math.floor((total % 3600000) / 60000)
      const s = Math.floor((total % 60000) / 1000)
      const millis = total % 1000
      const pad = (n, w = 2) => String(n).padStart(w, '0')
      return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(millis, 3)}`
    },

    // Probed duration (MXF source) in seconds, formatted for display
    formatProbedSeconds (ms) {
      if (ms === null || ms === undefined) return '-'
      return this.formatDuration(Number(ms) / 1000)
    },

    // Format the probed_at timestamp (when the stitch worker ffprobed the segment)
    formatProbedAt (ts) {
      if (!ts) return 'unknown'
      try {
        return new Date(ts).toLocaleString()
      } catch (_) {
        return String(ts)
      }
    },

    async loadStorageConfigs () {
      try {
        this.storageConfigs = await storageBrowseApi.getConfigs()
        if (this.storageConfigs.length > 0) {
          this.selectedStorageConfigId = this.storageConfigs[0].id
          // Auto-load browser if in single file mode
          if (this.effectiveSourceType === 'single') {
            this.$nextTick(() => {
              this.browseSingleFileStorage('')
            })
          }
        }
      } catch (error) {
        console.error('Failed to load storage configs:', error)
      }
    },

    async loadProcessingWorkflows () {
      this.loadingWorkflows = true
      try {
        const workflows = await processingWorkflowsApi.getAll()
        this.processingWorkflows = workflows.filter(w => w.is_active)
        // Auto-select the default workflow if one exists
        const defaultWorkflow = this.processingWorkflows.find(w => w.is_default)
        if (defaultWorkflow) {
          this.selectedWorkflowId = defaultWorkflow.id
        } else if (this.processingWorkflows.length === 1) {
          // If only one workflow, auto-select it
          this.selectedWorkflowId = this.processingWorkflows[0].id
        }
      } catch (error) {
        console.error('Failed to load processing workflows:', error)
        // Non-fatal - workflows are optional
      } finally {
        this.loadingWorkflows = false
      }
    },

    async loadPlatformWorkflowTemplates () {
      this.loadingPlatformWorkflows = true
      try {
        const templates = await stitchJobsApi.getAvailableWorkflows()
        // Filter to workflows that support segmented input (for stitching)
        this.platformWorkflowTemplates = templates.filter(t =>
          t.supports_segmented_input || t.supports_single_file_input
        )
        // Auto-select ts_library_to_tx as default (for TS files)
        const tsWorkflow = this.platformWorkflowTemplates.find(t => t.template_code === 'ts_library_to_tx')
        if (tsWorkflow) {
          this.selectedPlatformWorkflowId = tsWorkflow.id
        } else if (this.platformWorkflowTemplates.length > 0) {
          this.selectedPlatformWorkflowId = this.platformWorkflowTemplates[0].id
        }
      } catch (error) {
        console.error('Failed to load platform workflow templates:', error)
        // Non-fatal - platform templates are optional
      } finally {
        this.loadingPlatformWorkflows = false
      }
    },

    getPlatformWorkflowDescription (templateId) {
      const template = this.platformWorkflowTemplates.find(t => t.id === templateId)
      if (!template) return ''

      const parts = []
      if (template.description) {
        parts.push(template.description)
      }
      if (template.supported_input_formats && template.supported_input_formats.length) {
        parts.push(`Input: ${template.supported_input_formats.join(', ')}`)
      }
      return parts.join(' | ')
    },

    getWorkflowDescription (workflowId) {
      const workflow = this.processingWorkflows.find(w => w.id === workflowId)
      if (!workflow) return ''

      const parts = []
      if (workflow.description) {
        parts.push(workflow.description)
      }
      if (workflow.encoding_preset_name) {
        parts.push(`Preset: ${workflow.encoding_preset_name}`)
      }
      if (workflow.output_config_name) {
        parts.push(`Output: ${workflow.output_config_name}`)
      }
      return parts.join(' | ')
    },

    async loadData () {
      this.loading = true
      try {
        this.segments = await segmentsApi.getAllByEpisodeId(this.episodeId)
        // Also load QC status if available
        await this.loadQcStatus()
        // Auto-detect if this is a single file episode
        this.detectEpisodeType()
        // Fetch episode info for pattern learning if not provided via props
        if (!this.showTitleProp) {
          await this.loadEpisodeInfo()
        }
      } catch (error) {
        console.error('Failed to load segments:', error)
        this.$buefy.toast.open({
          message: 'Failed to load segments',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },

    async loadEpisodeInfo () {
      try {
        const episode = await episodesApi.get(this.episodeId)
        this.episodeInfo = episode
      } catch (error) {
        console.warn('Could not load episode info for pattern learning:', error)
        // Non-critical - pattern learning will still work with partial info
      }
    },

    // Detect if this episode should use single or multi-segment mode
    // Check if all segments point to the same file (single file mode)
    detectEpisodeType () {
      if (this.segments.length === 0) {
        this.episodeSourceType = 'multi' // Default for new episodes
        return
      }

      // Check if all linked segments have the same source file
      const linkedSegments = this.segments.filter(s => s.source_filename)
      if (linkedSegments.length > 0) {
        const firstFile = linkedSegments[0].source_filename
        const allSameFile = linkedSegments.every(s => s.source_filename === firstFile)
        // If all segments link to the same file, it's single file mode
        if (allSameFile && linkedSegments.length === this.segments.length) {
          this.episodeSourceType = 'single'
          return
        }
      }
      // Default to multi-segment mode
      this.episodeSourceType = 'multi'
    },

    onEpisodeTypeChange (type) {
      // Switching between single/multi mode
      const linkedSegments = this.segments.filter(s => s.source_filename)

      if (type === 'single' && linkedSegments.length > 0) {
        // Warn that switching will affect linked files
        this.$buefy.dialog.confirm({
          title: 'Switch to Single File Mode',
          message: 'This will treat the source as ONE complete file containing all segments already combined. Any existing segment file links will be replaced when you link a new complete file. Continue?',
          confirmText: 'Switch to Single File',
          cancelText: 'Cancel',
          type: 'is-info',
          onConfirm: () => {
            this.episodeSourceType = 'single'
            this.$emit('source-type-changed', 'single')
          },
          onCancel: () => {
            this.episodeSourceType = 'multi'
          }
        })
      } else if (type === 'multi' && this.singleFileLinked) {
        // Warn that switching from single to multi will require re-linking
        this.$buefy.dialog.confirm({
          title: 'Switch to Multiple Files Mode',
          message: 'This will require linking separate source files for each segment to be stitched together. Continue?',
          confirmText: 'Switch to Multiple Files',
          cancelText: 'Cancel',
          type: 'is-info',
          onConfirm: () => {
            this.episodeSourceType = 'multi'
            this.$emit('source-type-changed', 'multi')
          },
          onCancel: () => {
            this.episodeSourceType = 'single'
          }
        })
      } else {
        // Simple switch without confirmation (no conflicting linked files)
        this.episodeSourceType = type
        this.$emit('source-type-changed', type)
      }
    },

    // Get overall verification status for single file mode
    getOverallSingleFileStatus () {
      // If any segment has pending status, overall is pending
      if (this.segments.some(s => s.verification_status === 'pending')) {
        return 'pending'
      }
      // If all segments are matched or approved, overall is matched
      const allVerified = this.segments.every(s =>
        ['matched', 'mismatch_approved'].includes(s.verification_status)
      )
      if (allVerified) {
        return 'matched'
      }
      // If any segment has rejected status
      if (this.segments.some(s => s.verification_status === 'mismatch_rejected')) {
        return 'mismatch_rejected'
      }
      // Default to not linked if no segments have files
      return 'not_linked'
    },

    // Open file browser for single file mode (legacy - still used by modal)
    openSingleFileBrowser () {
      if (this.segments.length === 0) {
        this.$buefy.toast.open({
          message: 'Please create segments first in the Episode Segments section',
          type: 'is-warning'
        })
        return
      }
      this.browsingForSingleFile = true
      this.currentSegment = null // No specific segment - linking to all
      this.showFileBrowser = true
      this.browseTo('')
    },

    // Called when storage changes in single file mode
    onSingleFileStorageChange () {
      // Load the inline browser when storage changes
      this.singleFileBrowserPath = ''
      this.singleFileBrowserToken = null
      this.browseSingleFileStorage('')
    },

    // Browse storage for single file mode (inline panel)
    async browseSingleFileStorage (path, loadMore = false) {
      if (!this.selectedStorageConfigId) return

      if (loadMore) {
        this.loadingMoreSingleFiles = true
      } else {
        this.loadingSingleFileBrowser = true
        this.singleFileBrowserPath = path
        this.singleFileBrowserToken = null
        this.singleFileBrowserTruncated = false
      }

      try {
        const result = await storageBrowseApi.browse(
          this.selectedStorageConfigId,
          path,
          '', // prefix
          loadMore ? this.singleFileBrowserToken : null
        )

        if (loadMore) {
          // Append to existing results
          const newFolders = (result.folders || []).filter(f =>
            !this.singleFileBrowserFolders.some(existing => existing.path === f.path)
          )
          const newFiles = (result.files || []).filter(f =>
            !this.singleFileBrowserFiles.some(existing => existing.path === f.path)
          )
          this.singleFileBrowserFolders.push(...newFolders)
          this.singleFileBrowserFiles.push(...newFiles)
        } else {
          // Replace results
          this.singleFileBrowserFolders = result.folders || []
          this.singleFileBrowserFiles = result.files || []
        }

        // Backend returns `nextContinuationToken` (not `continuationToken`).
        // Reading the wrong field left this token undefined, so "Load More Files"
        // could never fetch page 2 — the single-file browse was hard-capped at
        // the first 1000 keys even when more existed.
        this.singleFileBrowserToken = result.nextContinuationToken
        this.singleFileBrowserTruncated = result.isTruncated || false
      } catch (error) {
        console.error('Failed to browse storage:', error)
        this.$buefy.toast.open({
          message: 'Failed to browse storage',
          type: 'is-danger'
        })
      } finally {
        this.loadingSingleFileBrowser = false
        this.loadingMoreSingleFiles = false
      }
    },

    // Load more files for single file browser
    async loadMoreSingleFiles () {
      if (this.singleFileBrowserToken) {
        await this.browseSingleFileStorage(this.singleFileBrowserPath, true)
      }
    },

    // Link a file from the inline browser to all segments
    async linkSingleFile (file) {
      if (this.segments.length === 0) {
        this.$buefy.toast.open({
          message: 'No segments to link to',
          type: 'is-warning'
        })
        return
      }

      try {
        await this.linkSingleFileToAllSegments(file)
      } catch (error) {
        // Error already handled in linkSingleFileToAllSegments
      }
    },

    // Truncate path for display
    truncateSingleFilePath (path, maxLength = 50) {
      if (!path) return ''
      if (path.length <= maxLength) return path
      const parts = path.split('/')
      const filename = parts.pop()
      if (filename.length >= maxLength) {
        return '...' + filename.slice(-maxLength + 3)
      }
      const remaining = maxLength - filename.length - 4
      if (remaining <= 0) return '.../' + filename
      return '.../' + parts.join('/').slice(-remaining) + '/' + filename
    },

    // Unlink single file from ALL segments
    async unlinkSingleFile () {
      if (!this.segments.length) return

      this.$buefy.dialog.confirm({
        title: 'Unlink Source File',
        message: 'This will unlink the source file from all segments. Continue?',
        confirmText: 'Unlink All',
        cancelText: 'Cancel',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            // Unlink from all segments
            for (const segment of this.segments) {
              if (segment.source_filename) {
                await stitchJobsApi.unlinkSourceFile(segment.id)
              }
            }
            this.$buefy.toast.open({
              message: 'Source file unlinked from all segments',
              type: 'is-info'
            })
            await this.loadData()
          } catch (error) {
            console.error('Failed to unlink file:', error)
            this.$buefy.toast.open({
              message: 'Failed to unlink file',
              type: 'is-danger'
            })
          }
        }
      })
    },

    // Link single file to ALL segments
    async linkSingleFileToAllSegments (file) {
      try {
        // Create link objects for all segments
        const links = this.segments.map(segment => ({
          segmentId: segment.id,
          storageConfigId: this.selectedStorageConfigId,
          sourcePath: file.path,
          sourceFilename: file.name
        }))

        // Bulk link to all segments
        const result = await stitchJobsApi.linkSourceFiles(links)

        this.$buefy.toast.open({
          message: `Linked ${file.name} to all segments`,
          type: 'is-success'
        })

        this.showFileBrowser = false
        this.browsingForSingleFile = false
        await this.loadData()

        // Emit files-linked event for parent components
        this.$emit('files-linked', { episodeId: this.episodeId, file, segmentCount: this.segments.length })

        return result
      } catch (error) {
        console.error('Failed to link file to all segments:', error)
        this.$buefy.toast.open({
          message: 'Failed to link file',
          type: 'is-danger'
        })
        throw error
      }
    },

    // Process single file (analyze and distribute durations across segments)
    async processSingleFile () {
      this.processingSingleFile = true
      try {
        // Recalculate timecodes - this will analyze the file and distribute duration
        const result = await stitchJobsApi.recalculateTimecodes(this.episodeId)

        const totalDuration = result.segments?.reduce((sum, s) => sum + (s.duration_seconds || 0), 0) || 0
        this.$buefy.toast.open({
          message: `File analyzed. Total duration: ${this.formatDuration(totalDuration)} distributed across all segments`,
          type: 'is-success',
          duration: 5000
        })
        // Reload to show updated data
        await this.loadData()
        // Emit event for parent
        this.$emit('single-file-processed', result)
      } catch (error) {
        console.error('Failed to process single file:', error)
        this.$buefy.toast.open({
          message: error?.data?.error || 'Failed to process file',
          type: 'is-danger'
        })
      } finally {
        this.processingSingleFile = false
      }
    },

    // Submit single file for encoding and QC (no stitching needed)
    async submitSingleFileForEncodeQc () {
      // Build confirmation message with workflow info
      const workflowName = this.selectedWorkflowId
        ? this.processingWorkflows.find(w => w.id === this.selectedWorkflowId)?.workflow_name
        : null
      const workflowInfo = workflowName ? ` using "${workflowName}" workflow` : ''

      this.$buefy.dialog.confirm({
        title: 'Submit for Encode & QC',
        message: `This will submit the complete source file for encoding and quality control checks${workflowInfo}. Continue?`,
        confirmText: 'Submit',
        type: 'is-success',
        onConfirm: async () => {
          this.submittingEncodeJob = true
          try {
            // Use the stitch job endpoint but flag it as a single file (no actual stitching needed)
            const result = await stitchJobsApi.submitJob(this.episodeId, {
              processingWorkflowId: this.selectedWorkflowId,
              skipStitch: true
            })

            this.$buefy.toast.open({
              message: 'File submitted for encoding and QC',
              type: 'is-success',
              duration: 5000
            })

            // Reload QC status
            await this.loadQcStatus()

            // Emit event for parent
            this.$emit('encode-job-submitted', result)
          } catch (error) {
            console.error('Failed to submit encode job:', error)
            this.$buefy.toast.open({
              message: error?.data?.error || 'Failed to submit for encoding',
              type: 'is-danger'
            })
          } finally {
            this.submittingEncodeJob = false
          }
        }
      })
    },

    async loadQcStatus () {
      try {
        const status = await stitchJobsApi.getQcStatus(this.episodeId)
        // Show QC panel if:
        // 1. Stitch was submitted (multi-segment mode), OR
        // 2. QC result exists (single-file mode with direct QC)
        const hasStitchSubmitted = status.stitch && status.stitch.status && status.stitch.status !== 'not_started'
        const hasQcResult = status.qc && status.qc.status && status.qc.resultId
        if (hasStitchSubmitted || hasQcResult) {
          this.qcStatus = status
        } else {
          this.qcStatus = null
        }
        // Emit QC status change event
        this.$emit('qc-status-changed', this.qcStatus)
      } catch (error) {
        // QC status not available yet - that's fine
        this.qcStatus = null
        this.$emit('qc-status-changed', null)
      }
    },

    // ==========================================
    // Learn Pattern from Example
    // ==========================================

    openLearnPatternModal () {
      // Pre-fill with episode info if available
      const firstSegment = this.segments[0]
      if (firstSegment) {
        this.exampleEpisodeNumber = firstSegment.episode_number || null
        this.exampleSegmentNumber = 1
      }
      this.exampleFilename = ''
      this.learnedPattern = null
      this.learnedPatternConfidence = 0
      this.learnedPatternMatchedParts = null
      this.showLearnPatternModal = true
    },

    async learnPattern () {
      if (!this.exampleFilename) {
        this.$buefy.toast.open({
          message: 'Please enter an example filename',
          type: 'is-warning'
        })
        return
      }

      this.learningPattern = true
      try {
        const result = await storageBrowseApi.learnPatternFromExample(
          this.exampleFilename,
          this.showTitle || '',
          this.seasonNumber || null,
          this.exampleEpisodeNumber || null,
          this.exampleSegmentNumber || null
        )

        if (result.success) {
          this.learnedPattern = result.pattern
          this.learnedPatternConfidence = result.confidence
          this.learnedPatternMatchedParts = result.matchedParts

          if (result.confidence < 0.5) {
            this.$buefy.toast.open({
              message: 'Low confidence pattern - some parts could not be identified',
              type: 'is-warning'
            })
          }
        } else {
          this.$buefy.toast.open({
            message: 'Could not learn pattern from this filename',
            type: 'is-danger'
          })
        }
      } catch (error) {
        console.error('Failed to learn pattern:', error)
        this.$buefy.toast.open({
          message: 'Failed to analyze filename pattern',
          type: 'is-danger'
        })
      } finally {
        this.learningPattern = false
      }
    },

    applyLearnedPattern () {
      if (this.learnedPattern) {
        this.$buefy.toast.open({
          message: `Pattern learned: ${this.learnedPattern}`,
          type: 'is-success'
        })
        this.showLearnPatternModal = false
        // The pattern is stored and will be available for auto-suggest to use
      }
    },

    clearLearnedPattern () {
      this.learnedPattern = null
      this.learnedPatternConfidence = 0
      this.learnedPatternMatchedParts = null
      this.learnedPath = ''
      this.$buefy.toast.open({
        message: 'Learned pattern and path cleared',
        type: 'is-info'
      })
    },

    async autoSuggestFiles () {
      if (!this.selectedStorageConfigId) {
        this.$buefy.toast.open({
          message: 'Please select a storage first',
          type: 'is-warning'
        })
        return
      }

      this.suggesting = true
      try {
        // Pass learned pattern (season-specific) if available - complements storage config pattern
        const result = await storageBrowseApi.suggestSegmentFiles(
          this.episodeId,
          this.selectedStorageConfigId,
          this.learnedPath, // optional subfolder path to limit search
          this.learnedPattern // customPattern - learned from example, season-specific
        )

        // Link suggested files
        const linksToCreate = result.suggestions
          .filter(s => s.suggestedFile)
          .map(s => ({
            segmentId: s.segment.id,
            storageConfigId: this.selectedStorageConfigId,
            sourcePath: s.suggestedFile.path,
            sourceFilename: s.suggestedFile.name
          }))

        if (linksToCreate.length === 0) {
          this.$buefy.toast.open({
            message: 'No matching files found. Try browsing manually.',
            type: 'is-warning'
          })
          return
        }

        // Bulk link files
        const linkResult = await stitchJobsApi.linkSourceFiles(linksToCreate)

        this.$buefy.toast.open({
          message: `Linked ${linkResult.results.length} files`,
          type: 'is-success'
        })

        // Reload segments
        await this.loadData()
      } catch (error) {
        console.error('Failed to auto-suggest files:', error)
        this.$buefy.toast.open({
          message: 'Failed to suggest files',
          type: 'is-danger'
        })
      } finally {
        this.suggesting = false
      }
    },

    openFileBrowser (segment) {
      this.currentSegment = segment
      this.currentPath = ''
      this.manualPath = ''
      this.searchTerm = ''
      this.browseResults = []
      this.showFileBrowser = true
      this.browseStorage()
    },

    onStorageChange () {
      // Storage changed, could trigger re-suggest
    },

    async browseTo (path) {
      this.currentPath = path.replace(/\/$/, '') // Remove trailing slash
      this.manualPath = this.currentPath // Keep manual input in sync
      await this.browseStorage()
    },

    // Navigate up one folder level
    goUpOneLevel () {
      if (!this.currentPath) return
      const parts = this.currentPath.split('/').filter(p => p)
      parts.pop() // Remove last part
      this.browseTo(parts.join('/'))
    },

    // Navigate to a manually entered path
    browseToManualPath () {
      if (!this.manualPath) return
      // Clean the path - remove leading/trailing slashes
      const cleanPath = this.manualPath.trim().replace(/^\/+|\/+$/g, '')
      this.browseTo(cleanPath)
      // Keep the path in the input for reference
    },

    async browseStorage (loadMore = false) {
      if (!this.selectedStorageConfigId) return

      if (loadMore) {
        this.loadingMore = true
      } else {
        this.browsing = true
        // Reset pagination state when starting fresh
        this.nextContinuationToken = null
        this.isTruncated = false
        this.totalFoldersLoaded = 0
        this.totalFilesLoaded = 0
      }

      try {
        const result = await storageBrowseApi.browse(
          this.selectedStorageConfigId,
          this.currentPath,
          '', // prefix
          loadMore ? this.nextContinuationToken : null
        )

        if (loadMore) {
          // Append to existing results
          this.browseResults = [
            ...this.browseResults,
            ...result.folders,
            ...result.files
          ]
          this.totalFoldersLoaded += result.folders.length
          this.totalFilesLoaded += result.files.length
        } else {
          // Replace results
          this.browseResults = [...result.folders, ...result.files]
          this.totalFoldersLoaded = result.folders.length
          this.totalFilesLoaded = result.files.length
        }

        // Update pagination state
        this.isTruncated = result.isTruncated
        this.nextContinuationToken = result.nextContinuationToken
      } catch (error) {
        console.error('Failed to browse storage:', error)
        this.$buefy.toast.open({
          message: 'Failed to browse storage',
          type: 'is-danger'
        })
      } finally {
        this.browsing = false
        this.loadingMore = false
      }
    },

    async loadMoreResults () {
      if (!this.nextContinuationToken || this.loadingMore) return
      await this.browseStorage(true)
    },

    async searchFiles () {
      if (!this.searchTerm || !this.selectedStorageConfigId) return

      this.browsing = true
      try {
        const result = await storageBrowseApi.search(
          this.selectedStorageConfigId,
          this.searchTerm,
          this.currentPath
        )
        this.browseResults = result.results
      } catch (error) {
        console.error('Failed to search files:', error)
      } finally {
        this.browsing = false
      }
    },

    async selectFile (file) {
      // If in single file mode, link to all segments
      if (this.browsingForSingleFile) {
        await this.linkSingleFileToAllSegments(file)
        return
      }

      // Multi-segment mode - link to specific segment
      if (!this.currentSegment) return

      try {
        await stitchJobsApi.linkSourceFile(
          this.currentSegment.id,
          this.selectedStorageConfigId,
          file.path,
          file.name
        )

        this.$buefy.toast.open({
          message: `Linked ${file.name} to Segment ${this.currentSegment.segment_number}`,
          type: 'is-success'
        })

        this.showFileBrowser = false
        await this.loadData()
      } catch (error) {
        console.error('Failed to link file:', error)
        this.$buefy.toast.open({
          message: 'Failed to link file',
          type: 'is-danger'
        })
      }
    },

    async unlinkFile (segment) {
      this.$buefy.dialog.confirm({
        title: 'Unlink File',
        message: `Are you sure you want to unlink ${segment.source_filename} from Segment ${segment.segment_number}?`,
        confirmText: 'Unlink',
        type: 'is-warning',
        onConfirm: async () => {
          try {
            await stitchJobsApi.unlinkSourceFile(segment.id)
            this.$buefy.toast.open({
              message: 'File unlinked',
              type: 'is-success'
            })
            await this.loadData()
          } catch (error) {
            console.error('Failed to unlink file:', error)
            this.$buefy.toast.open({
              message: 'Failed to unlink file',
              type: 'is-danger'
            })
          }
        }
      })
    },

    async approveMismatch (segment) {
      try {
        await stitchJobsApi.approveMismatch(segment.id, 'Manual approval from UI')
        this.$buefy.toast.open({
          message: 'Duration mismatch approved',
          type: 'is-success'
        })
        await this.loadData()
      } catch (error) {
        console.error('Failed to approve mismatch:', error)
        this.$buefy.toast.open({
          message: 'Failed to approve mismatch',
          type: 'is-danger'
        })
      }
    },

    // Approve all segment mismatches (for single file mode)
    async approveAllSegmentMismatches () {
      try {
        const pendingSegments = this.segments.filter(s => s.verification_status === 'pending')
        for (const segment of pendingSegments) {
          await stitchJobsApi.approveMismatch(segment.id, 'Bulk approval - single file mode')
        }
        this.$buefy.toast.open({
          message: `Duration mismatch approved for all segments`,
          type: 'is-success'
        })
        await this.loadData()
      } catch (error) {
        console.error('Failed to approve mismatches:', error)
        this.$buefy.toast.open({
          message: 'Failed to approve mismatches',
          type: 'is-danger'
        })
      }
    },

    // Reject all segment mismatches (for single file mode)
    async rejectAllSegmentMismatches () {
      this.$buefy.dialog.confirm({
        title: 'Reject Duration Mismatch',
        message: 'Rejecting will unlink the source file from all segments. You will need to link the correct file.',
        confirmText: 'Reject',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            const pendingSegments = this.segments.filter(s => s.verification_status === 'pending')
            for (const segment of pendingSegments) {
              await stitchJobsApi.rejectMismatch(segment.id, 'Rejected - needs correct file')
            }
            this.$buefy.toast.open({
              message: 'Mismatch rejected - please link the correct file',
              type: 'is-info'
            })
            await this.loadData()
          } catch (error) {
            console.error('Failed to reject mismatches:', error)
            this.$buefy.toast.open({
              message: 'Failed to reject mismatches',
              type: 'is-danger'
            })
          }
        }
      })
    },

    async rejectMismatch (segment) {
      this.$buefy.dialog.confirm({
        title: 'Reject Duration Mismatch',
        message: `Rejecting will unlink the current file. You will need to upload/link the correct file.`,
        confirmText: 'Reject',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            await stitchJobsApi.rejectMismatch(segment.id, 'Rejected - needs correct file')
            this.$buefy.toast.open({
              message: 'Mismatch rejected - please link correct file',
              type: 'is-info'
            })
            await this.loadData()
          } catch (error) {
            console.error('Failed to reject mismatch:', error)
            this.$buefy.toast.open({
              message: 'Failed to reject mismatch',
              type: 'is-danger'
            })
          }
        }
      })
    },

    async submitStitchJob () {
      // Build confirmation message with workflow info
      const workflowName = this.selectedWorkflowId
        ? this.processingWorkflows.find(w => w.id === this.selectedWorkflowId)?.workflow_name
        : null
      const platformTemplateName = this.selectedPlatformWorkflowId
        ? this.platformWorkflowTemplates.find(t => t.id === this.selectedPlatformWorkflowId)?.template_name
        : null
      const workflowInfo = workflowName ? ` using "${workflowName}" workflow` : ''
      const platformInfo = platformTemplateName ? ` (${platformTemplateName})` : ''

      this.$buefy.dialog.confirm({
        title: 'Submit Stitch Job',
        message: `This will submit a job to stitch ${this.summary.total} segments into a single file${workflowInfo}${platformInfo}. Continue?`,
        confirmText: 'Submit Job',
        type: 'is-success',
        onConfirm: async () => {
          this.submittingJob = true
          try {
            const result = await stitchJobsApi.submitJob(this.episodeId, {
              processingWorkflowId: this.selectedWorkflowId,
              workflowTemplateId: this.selectedPlatformWorkflowId
            })
            this.$buefy.toast.open({
              message: `Stitch job submitted: ${result.segmentCount} segments`,
              type: 'is-success',
              duration: 5000
            })
            this.$emit('job-submitted', result)
          } catch (error) {
            console.error('Failed to submit stitch job:', error)
            this.$buefy.toast.open({
              message: error?.data?.error || 'Failed to submit stitch job',
              type: 'is-danger'
            })
          } finally {
            this.submittingJob = false
          }
        }
      })
    },

    async recalculateTimecodes () {
      this.recalculatingTimecodes = true
      try {
        const result = await stitchJobsApi.recalculateTimecodes(this.episodeId)
        this.$buefy.toast.open({
          message: `Timecodes recalculated for ${result.segmentCount} segments (${result.segmentGap}s gap)`,
          type: 'is-success',
          duration: 4000
        })
        // Reload segments to show updated timecodes
        await this.loadData()
        // Emit event so parent can update if needed
        this.$emit('timecodes-updated', result)
      } catch (error) {
        console.error('Failed to recalculate timecodes:', error)
        this.$buefy.toast.open({
          message: 'Failed to recalculate timecodes',
          type: 'is-danger'
        })
      } finally {
        this.recalculatingTimecodes = false
      }
    },

    // QC methods
    async openQcReport () {
      this.loadingQcReport = true
      try {
        const result = await stitchJobsApi.getQcReport(this.episodeId)
        // Open PDF in new tab
        window.open(result.url, '_blank')
      } catch (error) {
        console.error('Failed to get QC report:', error)
        this.$buefy.toast.open({
          message: 'Failed to load QC report',
          type: 'is-danger'
        })
      } finally {
        this.loadingQcReport = false
      }
    },

    async downloadQcReport () {
      this.loadingQcReport = true
      try {
        const result = await stitchJobsApi.getQcReport(this.episodeId)
        // Fetch the PDF and trigger download
        const response = await fetch(result.url)
        const blob = await response.blob()
        // Create download link
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        // Generate filename from result or use default
        const filename = result.filename || `QC-Report-Episode-${this.episodeId}.pdf`
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
        this.$buefy.toast.open({
          message: 'QC Report downloaded',
          type: 'is-success'
        })
      } catch (error) {
        console.error('Failed to download QC report:', error)
        this.$buefy.toast.open({
          message: 'Failed to download QC report',
          type: 'is-danger'
        })
      } finally {
        this.loadingQcReport = false
      }
    },

    async submitQcOverride () {
      this.processingQcAction = true
      try {
        await stitchJobsApi.overrideQc(this.episodeId, this.qcOverrideReason)
        this.$buefy.toast.open({
          message: 'QC override approved',
          type: 'is-success'
        })
        this.showQcOverrideModal = false
        this.qcOverrideReason = ''
        // Reload QC status
        await this.loadQcStatus()
        this.$emit('qc-updated', this.qcStatus)
        this.$emit('qc-status-changed', this.qcStatus)
      } catch (error) {
        console.error('Failed to override QC:', error)
        this.$buefy.toast.open({
          message: error?.data?.error || 'Failed to override QC',
          type: 'is-danger'
        })
      } finally {
        this.processingQcAction = false
      }
    },

    async submitQcReject () {
      this.processingQcAction = true
      try {
        await stitchJobsApi.rejectQc(this.episodeId, this.qcRejectReason)
        this.$buefy.toast.open({
          message: 'Stitch rejected - you can now re-stitch',
          type: 'is-info'
        })
        this.showQcRejectModal = false
        this.qcRejectReason = ''
        // Reload data and QC status
        await this.loadData()
        this.$emit('qc-rejected', this.episodeId)
      } catch (error) {
        console.error('Failed to reject QC:', error)
        this.$buefy.toast.open({
          message: error?.data?.error || 'Failed to reject stitch',
          type: 'is-danger'
        })
      } finally {
        this.processingQcAction = false
      }
    },

    getQcStatusType (status) {
      switch (status) {
        case 'passed': return 'is-success'
        case 'passed_with_warnings': return 'is-warning'
        case 'overridden': return 'is-info'
        case 'failed': return 'is-danger'
        case 'processing': return 'is-light'
        case 'pending': return 'is-light'
        default: return 'is-light'
      }
    },

    getQcStatusLabel (status) {
      switch (status) {
        case 'passed': return 'Passed'
        case 'passed_with_warnings': return 'Passed with Warnings'
        case 'overridden': return 'Overridden'
        case 'failed': return 'Failed'
        case 'processing': return 'Processing'
        case 'pending': return 'Pending'
        default: return status || 'Unknown'
      }
    },

    // Utility methods
    formatDuration (seconds) {
      if (!seconds) return '0s'
      const mins = Math.floor(seconds / 60)
      const secs = Math.round(seconds % 60)
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
    },

    formatDifference (seconds) {
      if (!seconds) return '0s'
      const sign = seconds >= 0 ? '+' : ''
      return `${sign}${seconds.toFixed(2)}s`
    },

    formatFileSize (bytes) {
      if (!bytes) return '-'
      const units = ['B', 'KB', 'MB', 'GB']
      let unitIndex = 0
      let size = bytes
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`
    },

    getDifferenceClass (diff) {
      if (Math.abs(diff) < 0.1) return 'has-text-success'
      if (Math.abs(diff) < 1) return 'has-text-warning'
      return 'has-text-danger'
    },

    getVerificationTagType (status) {
      switch (status) {
        case 'matched': return 'is-success'
        case 'mismatch_approved': return 'is-info'
        case 'pending': return 'is-warning'
        case 'mismatch_rejected': return 'is-danger'
        default: return 'is-light'
      }
    },

    getVerificationLabel (status) {
      switch (status) {
        case 'matched': return 'Matched'
        case 'mismatch_approved': return 'Approved'
        case 'pending': return 'Needs Approval'
        case 'mismatch_rejected': return 'Rejected'
        case 'not_linked': return 'Not Linked'
        default: return status
      }
    },

    isVideoFile (filename) {
      const ext = filename.split('.').pop().toLowerCase()
      return ['ts', 'mxf', 'mp4', 'mov', 'avi', 'mkv', 'm4v', 'mpg', 'mpeg'].includes(ext)
    },

    // Video preview methods
    async previewFile (file) {
      this.previewFilename = file.name
      this.previewFileData = file
      this.previewUrl = null
      this.previewError = null
      this.loadingPreview = true
      this.showVideoPreview = true

      try {
        // Get presigned URL for the file
        const result = await storageBrowseApi.getPresignedUrl(
          this.selectedStorageConfigId,
          file.path
        )
        this.previewUrl = result.url
      } catch (error) {
        console.error('Failed to get preview URL:', error)
        this.previewError = 'Failed to load video preview. The file format may not be supported for browser playback.'
      } finally {
        this.loadingPreview = false
      }
    },

    closeVideoPreview () {
      // Pause video if playing
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
      }
      this.showVideoPreview = false
      this.previewUrl = null
      this.previewFilename = ''
      this.previewFileData = null
      this.previewError = null
    },

    handleVideoError () {
      this.previewError = 'Failed to play video. The file format (.ts, .mxf) may not be supported for browser playback. Try downloading the file to preview locally.'
    },

    async selectFileFromPreview () {
      if (!this.previewFileData || !this.currentSegment) return

      // Close preview and link the file
      this.closeVideoPreview()
      await this.selectFile(this.previewFileData)
    }
  }
}
</script>

<style scoped>
.segment-source-linker {
  padding: 1rem;
  background: #fafafa;
  border-radius: 6px;
}

.segments-link-table {
  color: #363636 !important;
}

.segments-link-table >>> td,
.segments-link-table >>> th {
  color: #363636 !important;
  vertical-align: middle !important;
}

.stitch-actions {
  padding-top: 1rem;
  border-top: 1px solid #dbdbdb;
}

.file-browser-list {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.modal-card-body {
  color: #363636;
}

/* Video preview styles */
.video-preview-body {
  padding: 0;
  background: #000;
}

.video-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.video-player {
  max-width: 100%;
  max-height: 540px;
  width: 100%;
}

.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mt-4 {
  margin-top: 1rem;
}

/* QC Status Panel Styles */
.qc-status-panel .box {
  background: #ffffff;
  border: 1px solid #dbdbdb;
}

.qc-issues-list .notification {
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
}

.qc-issues-list .notification.is-small {
  font-size: 0.875rem;
}

/* Detection list styles for black frames, silence, freeze frames */
.detection-list {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
}

.detection-item {
  display: flex;
  align-items: center;
  padding: 0.35rem 0.5rem;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-size: 0.8rem;
  border-bottom: 1px solid #eeeeee;
}

.detection-item:last-child {
  border-bottom: none;
}

.detection-item:hover {
  background: #f0f0f0;
}

.has-margin-top-20 {
  margin-top: 20px;
}

.mb-0 {
  margin-bottom: 0 !important;
}

.mt-3 {
  margin-top: 0.75rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

/* Episode Type Toggle */
.episode-type-toggle {
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
}

/* Storage Browser Panel */
.storage-browser-panel {
  background: #fafafa;
  border: 1px solid #e0e0e0;
}

.storage-browser-panel .file-list-container table {
  margin-bottom: 0;
}

.storage-browser-panel .file-list-container th {
  position: sticky;
  top: 0;
  background: #f5f5f5;
  z-index: 1;
}

/* Single File Mode */
.single-file-mode .box {
  background: #ffffff;
  border: 1px solid #dbdbdb;
}

.single-file-info .notification {
  margin-bottom: 0;
}

/* Multi-Segment Mode */
.multi-segment-mode {
  margin-top: 0;
}

/* Folder Grid Styles */
.folder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  background: #fafafa;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.folder-item {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.folder-item:hover {
  background: #e8f4ff;
  border-color: #3273dc;
  transform: translateY(-1px);
}

.folder-item .icon {
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.folder-name {
  font-size: 0.85rem;
  color: #363636;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Files section header */
.files-section h6,
.folders-section h6 {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* Breadcrumb spacing fix */
.breadcrumb.mb-0 {
  margin-bottom: 0 !important;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
</style>
